import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, PaperClipIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import type { Client } from '@shared/types';
import { acordService, type AcordCertificate } from '../../services/acordService';
import { useClientContacts } from '../../hooks/useClientContacts';
import { useTemplates } from '../../hooks/useTemplates';
import { RichTextEditor } from '../RichTextEditor';

interface EmailAcordModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: Client;
  certificate: AcordCertificate | null;
}

export function EmailAcordModal({ isOpen, onClose, client, certificate }: EmailAcordModalProps) {
  const { contacts, loading: contactsLoading } = useClientContacts(client.id);
  const { templates } = useTemplates();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [includeW9, setIncludeW9] = useState(false);
  const [selectedW9Id, setSelectedW9Id] = useState<string>('');

  const contactsWithEmail = useMemo(
    () => contacts.filter((c) => !!c.email),
    [contacts],
  );

  const w9Templates = useMemo(
    () => templates.filter((t) => t.templateType === 'W9' && t.isActive),
    [templates],
  );

  // Track the last subject + body we auto-applied so we can refresh them
  // when the W-9 toggle changes — but only if the user hasn't typed over
  // them. Otherwise we'd clobber edits.
  const lastDefaultBodyRef = useRef('');
  const lastDefaultSubjectRef = useRef('');

  const buildDefaultSubject = (clientName: string, withW9: boolean): string =>
    withW9
      ? `Vendor Onboarding Documents — ${clientName}`
      : `Insurance Certificate — ${clientName}`;

  const buildDefaultBody = (clientName: string, withW9: boolean): string => {
    if (withW9) {
      // Vendor-onboarding framing: cert + W-9 together is the typical
      // package new vendors send to a client's AP/procurement team.
      return (
        `<p>Hi,</p>` +
        `<p>For your vendor onboarding records, attached please find our insurance certificate (naming <strong>${clientName}</strong> as certificate holder) along with our completed W-9.</p>` +
        `<p>Let me know if you need any additional documentation to complete the setup.</p>` +
        `<p>Thanks,</p>`
      );
    }
    return (
      `<p>Hi,</p>` +
      `<p>Attached is the insurance certificate naming <strong>${clientName}</strong> as certificate holder.</p>` +
      `<p>Let me know if anything looks off or if you need additional coverage information.</p>` +
      `<p>Thanks,</p>`
    );
  };

  // Reset state every time the modal opens, since it's reused across certs.
  useEffect(() => {
    if (!isOpen) return;
    setSelectedIds(new Set());
    const initialSubject = buildDefaultSubject(client.name, false);
    const initialBody = buildDefaultBody(client.name, false);
    setSubject(initialSubject);
    setBody(initialBody);
    lastDefaultSubjectRef.current = initialSubject;
    lastDefaultBodyRef.current = initialBody;
    setIncludeW9(false);
    setSelectedW9Id('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, client.name, certificate?.file_id]);

  // Refresh subject + body when the W-9 toggle flips — but only on fields
  // the user hasn't touched. Once they've edited either field, leave that
  // field alone forever.
  useEffect(() => {
    if (!isOpen) return;

    if (subject === lastDefaultSubjectRef.current) {
      const nextSubject = buildDefaultSubject(client.name, includeW9);
      if (nextSubject !== subject) {
        setSubject(nextSubject);
        lastDefaultSubjectRef.current = nextSubject;
      }
    }

    if (body === lastDefaultBodyRef.current) {
      const nextBody = buildDefaultBody(client.name, includeW9);
      if (nextBody !== body) {
        setBody(nextBody);
        lastDefaultBodyRef.current = nextBody;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeW9]);

  // If there's exactly one W9 template, default-select it when the toggle
  // is enabled so the user doesn't have to pick.
  useEffect(() => {
    if (!includeW9) return;
    if (w9Templates.length === 1 && !selectedW9Id) {
      setSelectedW9Id(w9Templates[0].id);
    }
  }, [includeW9, w9Templates, selectedW9Id]);

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSend = async () => {
    if (!certificate) return;
    if (selectedIds.size === 0) {
      toast.error('Select at least one recipient.');
      return;
    }
    if (includeW9 && !selectedW9Id) {
      toast.error('Pick which W-9 form to attach.');
      return;
    }
    setSending(true);
    const toastId = toast.loading('Sending email…');
    try {
      const result = await acordService.emailCertificate({
        clientId: client.id,
        fileId: certificate.file_id,
        toContactIds: Array.from(selectedIds),
        subject,
        bodyHtml: body,
        additionalTemplateIds: includeW9 && selectedW9Id ? [selectedW9Id] : [],
      });
      const names = result.recipients.map((r) => r.name).join(', ');
      const count = result.attachments?.length ?? 1;
      toast.success(
        `Sent ${count} attachment${count === 1 ? '' : 's'} to ${names}`,
        { id: toastId },
      );
      onClose();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to send email',
        { id: toastId },
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={sending ? () => {} : onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white shadow-xl transition-all">
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">
                    Email ACORD Certificate
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    disabled={sending}
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="px-6 py-4 space-y-4">
                  {/* Recipients */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                    {contactsLoading ? (
                      <p className="text-sm text-gray-500">Loading contacts…</p>
                    ) : contactsWithEmail.length === 0 ? (
                      <p className="text-sm text-gray-500">
                        No contacts with an email address. Add one on the client first.
                      </p>
                    ) : (
                      <div className="border border-gray-200 rounded-md divide-y divide-gray-100 max-h-44 overflow-y-auto">
                        {contactsWithEmail.map((c) => (
                          <label
                            key={c.id}
                            className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50"
                          >
                            <input
                              type="checkbox"
                              checked={selectedIds.has(c.id)}
                              onChange={() => toggle(c.id)}
                              className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                            />
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-medium text-gray-900">
                                {c.name}
                                <span className="ml-2 text-xs font-normal text-gray-500">
                                  · {c.contactType}
                                </span>
                              </p>
                              <p className="truncate text-xs text-gray-500">{c.email}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Subject */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                    <input
                      type="text"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  {/* Body */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Message
                    </label>
                    <RichTextEditor
                      value={body}
                      onChange={setBody}
                      minHeight={180}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Your saved email signature is appended automatically. The editor produces email-safe HTML (paragraphs, bold/italic/underline, lists, links).
                    </p>
                  </div>

                  {/* Attachments */}
                  {certificate && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Attachments
                      </label>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-700">
                          <PaperClipIcon className="w-4 h-4 text-gray-400" />
                          <span className="truncate">{certificate.name}</span>
                        </div>

                        {/* W-9 attach option. Hidden entirely if no active
                            W9 template is registered — the Template Manager
                            is the place to set that up. */}
                        {w9Templates.length > 0 && (
                          <div className="px-3 py-2 border border-gray-200 rounded-md">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={includeW9}
                                onChange={(e) => setIncludeW9(e.target.checked)}
                                className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                              />
                              <PaperClipIcon className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-gray-700">Include W-9 form</span>
                            </label>
                            {includeW9 && w9Templates.length > 1 && (
                              <select
                                value={selectedW9Id}
                                onChange={(e) => setSelectedW9Id(e.target.value)}
                                className="mt-2 ml-7 w-[calc(100%-1.75rem)] px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                              >
                                <option value="">Select a W-9 form…</option>
                                {w9Templates.map((t) => (
                                  <option key={t.id} value={t.id}>
                                    {t.name}
                                  </option>
                                ))}
                              </select>
                            )}
                            {includeW9 && w9Templates.length === 1 && (
                              <p className="mt-1 ml-7 text-xs text-gray-500">
                                {w9Templates[0].name}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-6 py-3">
                  <button
                    onClick={onClose}
                    disabled={sending}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSend}
                    disabled={sending || selectedIds.size === 0 || contactsWithEmail.length === 0}
                    className="px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
                  >
                    {sending ? 'Sending…' : 'Send'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
